import { type LatLng, type Location, type NearbyLocation } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash-es'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, View } from 'react-native'
import Animated, { FadeInLeft } from 'react-native-reanimated'
import { useSpinDelay } from 'spin-delay'
import { LocationAutocomplete } from '~/components/locations/LocationAutocomplete'
import { toLocationKey } from '~/components/locations/utils'
import { DisplayValue, Label } from '~/components/typography/Content'
import { Button, ButtonLabel } from '~/components/ui/button'
import { type InputVariant } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { useUserId } from '~/components/user/SessionProvider'
import designTokens from '~/lib/designTokens'
import { trpc } from '~/lib/trpc'
import { cn } from '~/lib/utils'

type ChipItem = {
  key: string
  location: Location
  selected: boolean
}

function dedupeLocations(chips: ChipItem[]) {
  return uniqBy(chips, (c) => c.location.label)
}

function getDistanceFromCoordinates(coord1: LatLng, coord2: LatLng): number {
  const latDiff = coord1.lat - coord2.lat
  const lngDiff = coord1.lng - coord2.lng
  return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff)
}

function sortByDistance(chips: ChipItem[], referenceCoordinates: LatLng): ChipItem[] {
  return chips.slice().sort((a, b) => {
    const aDist = a.location.distance
    const bDist = b.location.distance

    if (typeof aDist === 'number' && typeof bDist === 'number') {
      return aDist - bDist
    }

    if (typeof aDist === 'number') return -1
    if (typeof bDist === 'number') return 1

    const distanceA = getDistanceFromCoordinates(
      a.location.coordinates ?? referenceCoordinates,
      referenceCoordinates
    )
    const distanceB = getDistanceFromCoordinates(
      b.location.coordinates ?? referenceCoordinates,
      referenceCoordinates
    )

    return distanceA - distanceB
  })
}

function getSortedChips(chips: ChipItem[], centerIndex: number) {
  const selected = chips.filter((location) => location.selected)
  const center = chips[centerIndex]

  const rightUnselected = chips.slice(centerIndex + 1).filter((location) => !location.selected)

  return dedupeLocations([
    ...selected,
    center,
    ...sortByDistance(rightUnselected, center.location.coordinates ?? { lat: 0, lng: 0 }).slice(
      0,
      10
    ),
  ])
}

export function SelectLocations({
  initialLocations,
  onChangeLocations,
  countryCode,
  inputVariant,
  biasCoordinates,
}: {
  initialLocations: NearbyLocation[]
  onChangeLocations: (locations: NearbyLocation[]) => void
  countryCode?: string
  inputVariant?: InputVariant
  biasCoordinates?: LatLng | null
}) {
  const { t } = useTranslations('interest')
  const MAX_SELECTED = 10

  const lastEmittedSelectedKeysRef = useRef<string[]>([])
  const onChangeRef = useRef(onChangeLocations)

  useEffect(() => {
    onChangeRef.current = onChangeLocations
  }, [onChangeLocations])

  const [chips, setChips] = useState<ChipItem[]>(() =>
    dedupeLocations(
      initialLocations.map((loc) => ({
        key: toLocationKey(loc),
        location: loc,
        selected: true,
      }))
    )
  )

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
    const selectedLocations = chips.filter((chip) => chip.selected).map((chip) => chip.location)

    lastEmittedSelectedKeysRef.current = selectedLocations.map((loc) => toLocationKey(loc))

    onChangeRef.current(selectedLocations)
  }, [chips])

  const [selectedKey, setSelectedKey] = useState<string | null>(
    initialLocations[0] ? toLocationKey(initialLocations[0]) : null
  )

  useEffect(() => {
    if (!initialLocations[0]) {
      return
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    const incomingKeys = initialLocations.map((loc) => toLocationKey(loc))

    if (arraysEqual(incomingKeys, lastEmittedSelectedKeysRef.current)) {
      return
    }

    setChips(
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      initialLocations.map((loc) => ({
        key: toLocationKey(loc),
        location: loc,
        selected: true,
      }))
    )

    setSelectedKey(toLocationKey(initialLocations[0]))
  }, [initialLocations])

  const prevSelectedLocation = useMemo(
    () => chips.filter((c) => c.key === selectedKey && c.selected).at(-1),
    [chips, selectedKey]
  )

  const userId = useUserId()
  const initialCoordinates = initialLocations[0]?.coordinates

  const nearbyLocationsParams = {
    coordinates:
      prevSelectedLocation?.location.coordinates ?? initialCoordinates ?? biasCoordinates ?? null,
    limit: 30,
    countryCode: countryCode ?? 'fi',
    sessionToken:
      userId || initialLocations[0]?.label || prevSelectedLocation?.location.label || 'anonymous',
  }

  const isDisabled = chips.filter((c) => c.selected).length >= MAX_SELECTED

  const { data: nearby, isLoading: _isLoadingNearby } = useQuery(
    trpc.locations.getLocations.queryOptions(nearbyLocationsParams, {
      enabled: !!(prevSelectedLocation?.location.coordinates || initialCoordinates) && !isDisabled,
    })
  )

  const isLoadingNearby = useSpinDelay(_isLoadingNearby, {
    delay: 200,
    minDuration: 700,
  })

  const referenceKey =
    prevSelectedLocation?.key ?? (initialLocations[0] ? toLocationKey(initialLocations[0]) : null)

  useEffect(() => {
    if (!nearby?.length) {
      return
    }

    setChips((prev) => {
      let next = prev

      const existingKeys = new Set(next.map((location) => location.key))
      const candidates = nearby.slice().sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0))

      const picks: ChipItem[] = []

      for (const loc of candidates) {
        const key = toLocationKey(loc)

        if (key === referenceKey) continue

        if (!existingKeys.has(key) || !next.find((l) => l.key === key)?.selected) {
          picks.push({ key, location: loc, selected: false })
        }

        if (picks.filter((p) => !p.selected).length === 5) {
          break
        }
      }

      next = [...next.filter((l) => !picks.some((p) => p.key === l.key)), ...picks]

      const idx = next.findIndex((location) => location.key === referenceKey)

      if (idx === -1) {
        return next
      }

      return getSortedChips(next, idx)
    })
  }, [nearby, referenceKey])

  const toggle = useCallback(
    (chip: ChipItem) => {
      if (isDisabled && !chip.selected) return

      if (!isDisabled && !chip.selected) {
        setSelectedKey(chip.key)
      }

      setChips((prev) => {
        const selectedCount = prev.filter((location) => location.selected).length

        let locationWillBeSelected = false

        let next = prev.map((location) => {
          if (location.key !== chip.key) return location
          locationWillBeSelected = !location.selected

          if (locationWillBeSelected && isDisabled) {
            locationWillBeSelected = false
            return location
          }

          return { ...location, selected: locationWillBeSelected }
        })

        if (!locationWillBeSelected) {
          return next
        }

        const selected = next.filter((l) => l.selected)
        const unselected = next.filter((l) => !l.selected)
        next = [...selected, ...unselected]

        const nextCount = selectedCount + 1

        if (nextCount === MAX_SELECTED) {
          const idx = next.findIndex((l) => l.key === chip.key)

          if (idx === -1) {
            return next
          }

          return getSortedChips(next, idx)
        }

        return next
      })
    },
    [isDisabled]
  )

  const selectedCount = chips.filter((c) => c.selected).length

  return (
    <Animated.View className="relative w-full gap-2" entering={FadeInLeft}>
      <LocationAutocomplete
        testIdBase="interest-locations"
        bias={
          prevSelectedLocation?.location.coordinates ??
          biasCoordinates ??
          initialLocations[0]?.coordinates ??
          null
        }
        inputVariant={inputVariant}
        countryCode={
          // eslint-disable-next-line i18next/no-literal-string
          countryCode ?? 'fi'
        }
        placeholder={
          isDisabled
            ? t(($) => $.locationSelection.maxReached)
            : t(($) => $.locationSelection.placeholder)
        }
        className={cn('mb-4', isDisabled && 'pointer-events-none opacity-50')}
        disabled={isDisabled}
        onSelect={(loc) => {
          if (isDisabled) return

          const key = toLocationKey(loc)
          setSelectedKey(key)

          setChips((prev) => {
            const existing = prev.find((c) => c.key === key)

            const next = existing
              ? prev.map((c) => (c.key === key ? { ...c, selected: true } : c))
              : [{ key, location: loc, selected: true }, ...prev]

            return getSortedChips(
              next,
              next.findIndex((c) => c.key === key)
            )
          })
        }}
      />
      <View className="relative z-10 flex-row flex-wrap gap-2">
        {chips.map((chip) => {
          if (isDisabled && !chip.selected) {
            return null
          }

          return (
            <Button
              key={chip.key}
              variant={chip.selected ? 'default' : 'outline'}
              size="sm"
              className="px-4"
              onPress={() => toggle(chip)}>
              <ButtonLabel className="flex-row items-center gap-1.5">
                {chip.location.label}
                {!!chip.location.postalCode && (
                  <Text
                    className={cn(
                      'text-xs text-secondary group-hover/button:text-accent',
                      chip.selected && 'text-neutral-200/80 group-hover/button:text-neutral-200/80'
                    )}>
                    {chip.location.postalCode}
                  </Text>
                )}
              </ButtonLabel>
            </Button>
          )
        })}
        {isLoadingNearby && (
          <View className="size-10 items-center justify-center self-center rounded-full">
            <ActivityIndicator size="small" color={designTokens.secondary} />
          </View>
        )}
      </View>
      <View className="-mb-2 mt-3 flex-row items-center justify-between">
        <Label>{t(($) => $.labels.locations)}</Label>
        <DisplayValue className={cn(selectedCount >= MAX_SELECTED && 'text-accent')}>
          {selectedCount}/{MAX_SELECTED}
        </DisplayValue>
      </View>
      {selectedCount >= MAX_SELECTED && (
        <View>
          <Label className="text-right font-light text-sm text-accent">
            {t(($) => $.locationSelection.maxReachedLabel)}
          </Label>
        </View>
      )}
    </Animated.View>
  )
}

function arraysEqual<T>(a: T[], b: T[]) {
  if (a.length !== b.length) return false

  for (let index = 0; index < a.length; index++) {
    if (a[index] !== b[index]) return false
  }

  return true
}
